import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Flex,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@kognitos/lattice';

import type { V1AutomationInputSpec } from '@/generated/hooks/grimoire/models';
import { ScheduleTriggersDialog } from '@/modules/automation-details/components/schedules';
import { usePendingTriggerResume } from '@/modules/triggers/hooks/use-resume-trigger-connection';
import { EmailTrigger } from '../TriggerTypeSelector/EmailTrigger';
import { PollTrigger } from '../TriggerTypeSelector/PollTrigger';
import { TriggerType } from '../TriggerTypeSelector/TriggerType';
import { WebhookTrigger } from '../TriggerTypeSelector/WebhookTrigger';
import { ScheduleTriggerOption } from './ScheduleTriggerOption';

export interface IAddTriggerPickerProps {
  automationId: string;
  /** Automation display name, used in the mailbox address of a new email trigger */
  automationName?: string;
  inputSpecs: V1AutomationInputSpec[];
  hasWebhookTrigger: boolean;
  hasPollTrigger: boolean;
  /**
   * True while the workspace book catalog behind `hasWebhookTrigger` and
   * `hasPollTrigger` is still loading. The picker opens before it resolves, so
   * both options are shown disabled until then rather than omitted.
   */
  isLoadingTriggerTypes: boolean;
  hasSchedule: boolean;
  /**
   * Whether the user is allowed to create a schedule for this automation. Only
   * read for published automations, since drafts are never offered a schedule.
   */
  canCreateSchedule: boolean;
  /** True when a required input has no default, which a schedule can't supply. */
  scheduleNeedsDefaultInputs: boolean;
  /**
   * Whether the automation is an unpublished draft. Opt-in, and `false` by
   * default so the published details page keeps the behaviour it has always
   * had; the draft editor's rail passes `true` so the trigger dialogs write
   * against the draft rather than the published automation. A draft is also
   * never offered a schedule — only a published automation can be scheduled.
   */
  isDraft?: boolean;
  /**
   * Replaces the built-in "Add trigger" button that opens the popover. Opt-in
   * for surfaces that already label the affordance themselves — the draft
   * rail's Triggers section supplies a bare "+" because its own header is the
   * label. Omit it to keep the default labelled button. Must be a single
   * element: the popover clones it to attach its own trigger props.
   */
  trigger?: ReactElement;
}

type TTriggerDialog = 'email' | 'webhook' | 'poll' | 'schedule';

/**
 * "Add trigger" button and its popover of trigger types, with each type's
 * configuration dialog kept mounted so the popover can close on selection.
 */
export function AddTriggerPicker({
  automationId,
  automationName,
  inputSpecs,
  hasWebhookTrigger,
  hasPollTrigger,
  isLoadingTriggerTypes,
  hasSchedule,
  canCreateSchedule,
  scheduleNeedsDefaultInputs,
  isDraft = false,
  trigger,
}: IAddTriggerPickerProps) {
  const { t } = useTranslation('automation-inputs');
  const [isOpen, setIsOpen] = useState(false);
  const [activeDialog, setActiveDialog] = useState<
    TTriggerDialog | undefined
  >();

  // Mounts the dialog an OAuth return is owed without waiting for the
  // capability flags, which trail a catalog request of several seconds.
  const pendingResumeKind = usePendingTriggerResume();

  // Why the schedule option can't be used, if it can't. Doubles as the disabled
  // flag so the tooltip and the disabled state can never disagree.
  const scheduleDisabledReason = !canCreateSchedule
    ? t('triggerTypes.schedule.createDisabledTooltip')
    : scheduleNeedsDefaultInputs
      ? t('triggerTypes.schedule.disabledTooltip')
      : undefined;

  const closePicker = () => setIsOpen(false);
  const openDialog = (dialog: TTriggerDialog) => {
    setActiveDialog(dialog);
    closePicker();
  };
  // Opening is honoured too: the picker owns the open state, so it is the only
  // way a dialog can reopen itself after an OAuth sign-in.
  const handleDialogOpenChange = (dialog: TTriggerDialog, open: boolean) => {
    if (open) {
      setActiveDialog(dialog);
      return;
    }
    if (activeDialog === dialog) {
      setActiveDialog(undefined);
    }
  };

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          {trigger ?? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="add-trigger-button"
            >
              <Icon type="Plus" size="sm" />
              {t('triggers.add')}
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent
          align="start"
          collisionPadding={8}
          className="w-96 max-w-[calc(100vw-2rem)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-1"
          data-testid="add-trigger-picker"
        >
          <Flex direction="column" gap={0.5}>
            <TriggerType
              icon={<Icon type="Mail" size="md" />}
              title={t('triggerTypes.email.title')}
              description={t('triggerTypes.email.description')}
              onClick={() => openDialog('email')}
              presentation="list"
            />
            {/* Shown disabled while the catalog loads: the picker can be opened
                before it resolves, and silently omitting these would let the
                list grow under the user's cursor. */}
            {(isLoadingTriggerTypes || hasWebhookTrigger) && (
              <TriggerType
                icon={<Icon type="Webhook" size="md" />}
                title={t('triggerTypes.webhook.title')}
                description={t('triggerTypes.webhook.description')}
                onClick={() => openDialog('webhook')}
                disabled={isLoadingTriggerTypes}
                presentation="list"
              />
            )}
            {(isLoadingTriggerTypes || hasPollTrigger) && (
              <TriggerType
                icon={<Icon type="RefreshCw" size="md" />}
                title={t('triggerTypes.poll.title')}
                description={t('triggerTypes.poll.description')}
                onClick={() => openDialog('poll')}
                disabled={isLoadingTriggerTypes}
                presentation="list"
              />
            )}
            {!isDraft && !hasSchedule && (
              <ScheduleTriggerOption
                disabledReason={scheduleDisabledReason}
                onSelect={() => openDialog('schedule')}
              />
            )}
          </Flex>
        </PopoverContent>
      </Popover>

      <EmailTrigger
        inputSpecs={inputSpecs}
        automationId={automationId}
        automationName={automationName}
        isDraft={isDraft}
        hideTrigger
        open={activeDialog === 'email'}
        onOpenChange={open => handleDialogOpenChange('email', open)}
      />
      {(hasWebhookTrigger || pendingResumeKind === 'webhook') && (
        <WebhookTrigger
          inputSpecs={inputSpecs}
          automationId={automationId}
          isDraft={isDraft}
          hideTrigger
          open={activeDialog === 'webhook'}
          onOpenChange={open => handleDialogOpenChange('webhook', open)}
        />
      )}
      {(hasPollTrigger || pendingResumeKind === 'poll') && (
        <PollTrigger
          inputSpecs={inputSpecs}
          automationId={automationId}
          isDraft={isDraft}
          hideTrigger
          open={activeDialog === 'poll'}
          onOpenChange={open => handleDialogOpenChange('poll', open)}
        />
      )}
      {/* Not gated on hasSchedule: saving a new schedule flips hasSchedule to
          true, and unmounting the dialog at that moment would cut off its own
          close. The query behind it only runs while open. */}
      {!isDraft && (
        <ScheduleTriggersDialog
          automationId={automationId}
          open={activeDialog === 'schedule'}
          onOpenChange={open => handleDialogOpenChange('schedule', open)}
        />
      )}
    </>
  );
}
